import { useTranslation } from "react-i18next";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { AlertTriangleIcon } from "lucide-react";
import { StepComponentProps } from "./types";

/**
 * ConfirmationStepComponent component displays a confirmation view with accordions showing reasons.
 *
 * @param form - The React Hook Form instance
 * @param participatingGroups - All participating organizations
 * @param selectedOrganizations - Currently selected organizations
 */
export function ConfirmationStepComponent({
  form,
  participatingGroups,
  selectedOrganizations,
}: StepComponentProps) {
  const { t } = useTranslation(["translation", "common", "modals"]);
  const { watch } = form;
  const organizations = watch("organizations");

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <h3 className="text-lg font-semibold">
          {t("modals:updateparticipantsstepperform.step3.title")}
        </h3>
        <p className="text-sm text-gray-600">
          {t("modals:updateparticipantsstepperform.step3.description")}
        </p>
      </div>

      {/* Warning Alert */}
      <Alert className="border-amber-200 bg-amber-50">
        <AlertTriangleIcon className="h-4 w-4 text-amber-600" />
        <AlertTitle className="text-amber-800">
          {t("modals:confirmorganizationchanges.warning.title")}
        </AlertTitle>
        <AlertDescription className="text-amber-700">
          {selectedOrganizations.length === participatingGroups.length
            ? t("modals:confirmorganizationchanges.warning.allselected")
            : t("modals:confirmorganizationchanges.warning.partialselected")}
          <br />
          <br />
          <strong>{t("modals:confirmorganizationchanges.warning.reviewnote")}</strong>
        </AlertDescription>
      </Alert>

      {/* Selected Organizations with Accordions */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium text-gray-900">
            {t("modals:confirmorganizationchanges.organizations.affected")}
          </h4>
          <Badge variant="secondary" className="text-xs">
            {t("modals:confirmorganizationchanges.organizations.count", {
              selected: selectedOrganizations.length,
              total: participatingGroups.length,
            })}
          </Badge>
        </div>

        <Accordion type="single" collapsible className="w-full space-y-2">
          {organizations.map((org) => {
            // Find the full organization details
            const organization = selectedOrganizations.find(
              (selectedOrg) => selectedOrg.operationalOrganizationId === org.organizationId
            );

            if (!organization) {
              return null;
            }

            return (
              <AccordionItem
                key={org.organizationId}
                value={`org-${org.organizationId}`}
                className="border rounded-lg px-3 border-b last:border-b"
              >
                <AccordionTrigger className="hover:no-underline">
                  <span className="text-sm font-medium text-red-800">
                    {organization.operationalOrganizationName}
                  </span>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-2 pt-2">
                    <p className="text-xs font-medium text-gray-600">
                      {t("modals:updateparticipantsstepperform.step3.reasonlabel")}
                    </p>
                    <p className="text-sm text-gray-800 bg-gray-50 p-3 rounded-md">
                      {org.reason}
                    </p>
                  </div>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      </div>
    </div>
  );
}
